import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import MenuPage from "./pages/MenuPage";
import BoissonsPage from "./pages/BoissonsPage";
import ContactPage from "./pages/ContactPage";
import StaffLogin from "./pages/StaffLogin";
import StaffDashboard from "./pages/StaffDashboard";
import TablesManagement from "./pages/TablesManagement";
import OrdersManagement from "./pages/OrdersManagement";
import CustomerOrder from "./pages/CustomerOrder";
import KitchenDisplay from "./pages/KitchenDisplay";
import UsersManagement from "./pages/UsersManagement";
import AccountingPage from "./pages/AccountingPage";
import WebmasterConfig from "./pages/WebmasterConfig";
import DrinksManagement from "./pages/DrinksManagement";
import MenuManagement from "./pages/MenuManagement";
import PaymentHistory from "./pages/PaymentHistory";
import QRCodesPage from "./pages/QRCodesPage";
import VitrineConfig from "./pages/VitrineConfig";
import SaucesConfigPage from "./pages/SaucesConfigPage";
import StaffOrder from "./pages/StaffOrder";
import InvoiceTemplates from "./pages/InvoiceTemplates";
import StaffStats from "./pages/StaffStats";
import ReservationsPage from "./pages/ReservationsPage";
import ReservationConfirm from "./pages/ReservationConfirm";
import ReviewPage from "./pages/ReviewPage";
import ReviewsDashboard from "./pages/ReviewsDashboard";
import ClientsPage from "./pages/ClientsPage";
import PaymentMethodsConfig from "./pages/PaymentMethodsConfig";
import PrinterConfig from "./pages/PrinterConfig";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ContactPageEN from "./pages/ContactPageEN";
import MenuPageEN from "./pages/MenuPageEN";
import NotificationAlert from "./components/NotificationAlert";
import { Toaster } from "./components/ui/toaster";

// Staff notification wrapper - only renders for authenticated staff on staff routes
const StaffNotifications = () => {
  const location = useLocation();
  const token = localStorage.getItem('staffToken');
  const isStaffPage = location.pathname.startsWith('/staff') || location.pathname.startsWith('/kitchen');
  if (!token || !isStaffPage) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[9999]" data-testid="staff-notifications-wrapper">
      <NotificationAlert />
    </div>
  );
};

// Role permissions map
const ROLE_PAGES = {
  serveur:   ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/orders/new', '/staff/reservations'],
  caissier:  ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/qr-codes', '/staff/orders/new', '/staff/reservations'],
  cuisinier: ['/kitchen'],
  admin:     ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/qr-codes', '/kitchen', '/staff/payments', '/staff/accounting', '/staff/users', '/staff/vitrine', '/staff/sauces-config', '/staff/orders/new', '/staff/stats', '/staff/reservations', '/staff/reviews', '/staff/clients', '/staff/payment-methods'],
  webmaster: ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/qr-codes', '/kitchen', '/staff/payments', '/staff/accounting', '/staff/users', '/staff/vitrine', '/staff/config', '/staff/sauces-config', '/staff/orders/new', '/staff/invoice-templates', '/staff/stats', '/staff/reservations', '/staff/reviews', '/staff/clients', '/staff/payment-methods', '/staff/printer'],
};

// Redirect QR scan to homepage with table context
const QRRedirect = () => {
  const { qrCode } = useParams();
  return <Navigate to={`/?table=${qrCode}`} replace />;
};

const RoleGuard = ({ children, path }) => {
  const token = localStorage.getItem('staffToken');
  const role = localStorage.getItem('staffRole');
  if (!token) return <Navigate to="/staff/login" replace />;
  const allowed = ROLE_PAGES[role] || [];
  if (!allowed.includes(path)) return <Navigate to="/staff/dashboard" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<><Navbar /><Home /><Footer /></>} />
          <Route path="/menu" element={<><Navbar /><MenuPage /><Footer /></>} />
          <Route path="/boissons" element={<><Navbar /><BoissonsPage /><Footer /></>} />
          <Route path="/contact" element={<><Navbar /><ContactPage /><Footer /></>} />

          {/* QR scan redirects to homepage with table context */}
          <Route path="/order/:qrCode" element={<QRRedirect />} />
          {/* Actual order page */}
          <Route path="/commande/:qrCode" element={<CustomerOrder />} />
          <Route path="/reservation/confirm/:id/:token" element={<ReservationConfirm />} />
          <Route path="/avis" element={<ReviewPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/contact-anglais" element={<><Navbar /><ContactPageEN /><Footer /></>} />
          <Route path="/menu-anglais" element={<MenuPageEN />} />
          <Route path="/manu-anglais" element={<MenuPageEN />} />

          {/* Staff Auth */}
          <Route path="/staff/login" element={<StaffLogin />} />
          <Route path="/staff/dashboard" element={<StaffDashboard />} />

          {/* Staff - serveur, caissier, admin, webmaster */}
          <Route path="/staff/menu" element={<RoleGuard path="/staff/menu"><MenuManagement /></RoleGuard>} />
          <Route path="/staff/orders" element={<RoleGuard path="/staff/orders"><OrdersManagement /></RoleGuard>} />
          <Route path="/staff/orders/new" element={<RoleGuard path="/staff/orders/new"><StaffOrder /></RoleGuard>} />
          <Route path="/staff/tables" element={<RoleGuard path="/staff/tables"><TablesManagement /></RoleGuard>} />
          <Route path="/staff/drinks" element={<RoleGuard path="/staff/drinks"><DrinksManagement /></RoleGuard>} />

          {/* Staff - caissier, admin, webmaster */}
          <Route path="/staff/qr-codes" element={<RoleGuard path="/staff/qr-codes"><QRCodesPage /></RoleGuard>} />

          {/* Staff - admin, webmaster */}
          <Route path="/staff/payments" element={<RoleGuard path="/staff/payments"><PaymentHistory /></RoleGuard>} />
          <Route path="/staff/accounting" element={<RoleGuard path="/staff/accounting"><AccountingPage /></RoleGuard>} />
          <Route path="/staff/users" element={<RoleGuard path="/staff/users"><UsersManagement /></RoleGuard>} />
          <Route path="/staff/vitrine" element={<RoleGuard path="/staff/vitrine"><VitrineConfig /></RoleGuard>} />
          <Route path="/staff/sauces-config" element={<RoleGuard path="/staff/sauces-config"><SaucesConfigPage /></RoleGuard>} />
          <Route path="/staff/payment-methods" element={<RoleGuard path="/staff/payment-methods"><PaymentMethodsConfig /></RoleGuard>} />
          <Route path="/staff/printer" element={<RoleGuard path="/staff/printer"><PrinterConfig /></RoleGuard>} />

          {/* Webmaster only */}
          <Route path="/staff/config" element={<RoleGuard path="/staff/config"><WebmasterConfig /></RoleGuard>} />
          <Route path="/staff/invoice-templates" element={<RoleGuard path="/staff/invoice-templates"><InvoiceTemplates /></RoleGuard>} />
          <Route path="/staff/stats" element={<RoleGuard path="/staff/stats"><StaffStats /></RoleGuard>} />
          <Route path="/staff/reservations" element={<RoleGuard path="/staff/reservations"><ReservationsPage /></RoleGuard>} />
          <Route path="/staff/reviews" element={<RoleGuard path="/staff/reviews"><ReviewsDashboard /></RoleGuard>} />
          <Route path="/staff/clients" element={<RoleGuard path="/staff/clients"><ClientsPage /></RoleGuard>} />

          {/* Kitchen - cuisinier, admin, webmaster */}
          <Route path="/kitchen" element={<RoleGuard path="/kitchen"><KitchenDisplay /></RoleGuard>} />
        </Routes>
        <StaffNotifications />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
